import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';

/**
 * Props for the Input component.
 */
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Label text displayed above the input. */
  label?: string;
  /** Error message displayed below the input. Sets error styling when present. */
  error?: string;
  /** Helper text displayed below the input when no error is present. */
  helperText?: string;
  /** Icon or element rendered on the left inside the input. */
  leftIcon?: ReactNode;
  /** Icon or element rendered on the right inside the input. */
  rightIcon?: ReactNode;
  /** Additional CSS classes for the outer wrapper. */
  className?: string;
}

/**
 * Accessible input field with optional label, error state, helper text,
 * and left/right icon slots. Forwards ref to the underlying input element.
 *
 * @param props.label - Label text
 * @param props.error - Error message (also activates error styling)
 * @param props.helperText - Hint shown below the field
 * @param props.leftIcon - Icon slot on the left side of the input
 * @param props.rightIcon - Icon slot on the right side of the input
 * @example
 * ```tsx
 * <Input label="Email" type="email" error="Invalid email" />
 * <Input label="Search" leftIcon={<SearchIcon />} placeholder="Search..." />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, helperText, leftIcon, rightIcon, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const descriptionId = `${inputId}-description`;
  const hasError = error != null && error.length > 0;
  const hasDescription = hasError || (helperText != null && helperText.length > 0);

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label != null && (
        <label htmlFor={inputId} className="text-sm font-medium text-ink">
          {label}
        </label>
      )}
      <div className="relative flex items-center">
        {leftIcon != null && (
          <div className="pointer-events-none absolute left-3 flex items-center text-ink-muted">
            {leftIcon}
          </div>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-describedby={hasDescription ? descriptionId : undefined}
          aria-invalid={hasError}
          className={cn(
            'w-full rounded-md border bg-card-elevated px-3 py-2 text-sm text-ink placeholder:text-ink-muted',
            'focus:outline-none focus:ring-2 focus:ring-offset-1',
            'disabled:cursor-not-allowed disabled:opacity-50',
            hasError
              ? 'border-red-500 focus:ring-red-500'
              : 'border-ink/15 focus:ring-[var(--color-primary,theme(colors.blue.500))]',
            leftIcon != null && 'pl-10',
            rightIcon != null && 'pr-10',
          )}
          {...props}
        />
        {rightIcon != null && (
          <div className="pointer-events-none absolute right-3 flex items-center text-ink-muted">
            {rightIcon}
          </div>
        )}
      </div>
      {hasError && (
        <p id={descriptionId} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
      {!hasError && helperText != null && (
        <p id={descriptionId} className="text-xs text-ink-muted">
          {helperText}
        </p>
      )}
    </div>
  );
});
